// A repeating timer that keeps its cadence while the tab is in the BACKGROUND.
//
// Why this exists: Chrome subjects chained page timers (setInterval counts) to
// "intensive throttling" once the page has been hidden for a few minutes, is
// silent, and holds no WebRTC connection: the timer is then woken AT MOST ONCE
// PER MINUTE (https://developer.chrome.com/blog/timer-throttling-in-chrome-88).
// A 10-second poll therefore silently degrades into a 60-second one as soon as
// the pharmacist works in another tab. For order polling that is a delay; for
// incoming-consult discovery it is a missed call, because a caller who gives up
// takes the consult out of the discovery list before the next wake-up ever runs.
//
// Timers owned by a dedicated worker are scheduled by the worker's own thread
// and escape the page's throttling, so the worker is the primary clock. The
// page-side setInterval stays as a backup for environments that have no Worker
// (jsdom under tests, a restrictive CSP, a worker that fails to start) and for
// the day this browser behaviour changes. Both clocks call the same callback,
// and the coalescing window below drops the duplicate wake-up, so the callback
// still runs about once per interval no matter how many clocks are alive.

// A tick is ignored when it lands too soon after the previous one. Kept below 1
// so ordinary timer jitter never swallows a legitimate tick.
const COALESCE_RATIO = 0.8;

// Floor for the requested period. This clock exists to poll a backend on a
// schedule, so a period that arrived broken (NaN from an unparsed config, a
// negative, a zero) must not turn into an unthrottled request loop against the
// API. Slowing down is the safe direction to fail in; speeding up is not.
const MIN_PERIOD_MS = 1000;

interface TickWorker {
  dispose: () => void;
}

const createTickWorker = (
  intervalMs: number,
  onTick: () => void
): TickWorker | null => {
  if (
    typeof Worker === "undefined" ||
    typeof Blob === "undefined" ||
    typeof URL === "undefined" ||
    typeof URL.createObjectURL !== "function"
  ) {
    return null;
  }

  let objectUrl: string | null = null;
  try {
    // Built from a blob rather than a separate worker bundle: the tick worker
    // is three lines with no imports, and inlining it keeps the whole clock in
    // one file (no bundler entry point, no extra request, no cache skew
    // between the page and its worker after a deploy).
    const source = `setInterval(function () { postMessage(0); }, ${intervalMs});`;
    objectUrl = URL.createObjectURL(new Blob([source], { type: "text/javascript" }));
    const worker = new Worker(objectUrl);
    worker.onmessage = () => onTick();
    return {
      dispose: () => {
        worker.terminate();
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      },
    };
  } catch (error) {
    // Never fatal: the page-side interval still drives the callback, just with
    // the throttling this worker was meant to avoid.
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    console.warn("[backgroundInterval] worker clock unavailable:", error);
    return null;
  }
};

/**
 * Runs `callback` about every `intervalMs`, including while the page is hidden.
 * Returns the disposer; call it exactly once (further calls are harmless).
 */
export const setBackgroundInterval = (
  callback: () => void,
  intervalMs: number
): (() => void) => {
  // The value is interpolated into worker source, so only a sane number may
  // reach it. Anything invalid is clamped up to the floor, never down to zero.
  const requested = Math.floor(Number(intervalMs));
  const period =
    Number.isFinite(requested) && requested >= MIN_PERIOD_MS
      ? requested
      : MIN_PERIOD_MS;
  if (period !== requested) {
    console.warn(
      `[backgroundInterval] interval ${String(intervalMs)} is not usable, ` +
        `falling back to ${MIN_PERIOD_MS}ms`
    );
  }

  let disposed = false;
  let lastRun = Date.now();

  const fire = () => {
    if (disposed) return;
    const now = Date.now();
    if (now - lastRun < period * COALESCE_RATIO) return;
    lastRun = now;
    callback();
  };

  const timer = setInterval(fire, period);
  const worker = createTickWorker(period, fire);

  return () => {
    disposed = true;
    clearInterval(timer);
    worker?.dispose();
  };
};

export default setBackgroundInterval;
